// src/utils/sentry.js
import * as Sentry from "@sentry/react";

// Track errors we've already captured to prevent duplicates
const capturedErrors = new WeakSet();
// Also track by error message + stack for cases where error object is recreated
const capturedErrorSignatures = new Set();
// Store the last event ID from beforeSend (the real one Sentry uses)
let lastSentEventId = null;

// Initialize Sentry
export const initSentry = () => {
  // Only initialize if DSN is configured
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking disabled.");
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,

    // Set sample rates
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.2 : 1.0,

    // Only send errors in production, or if explicitly enabled
    enabled: process.env.NODE_ENV === "production" || process.env.REACT_APP_SENTRY_ENABLED === "true",

    // Enable built-in deduplication
    integrations: (integrations) => {
      return integrations;
    },

    // Filter out known non-critical errors and duplicates
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Skip if we've already captured this exact error instance
      if (error && capturedErrors.has(error)) {
        return null;
      }

      // Also check by signature (message + first line of stack) for recreated errors
      if (error?.message && error?.stack) {
        const signature = `${error.message}|${error.stack.split('\n')[1] || ''}`;
        if (capturedErrorSignatures.has(signature)) {
          return null;
        }
        // Mark this signature as captured
        capturedErrorSignatures.add(signature);
        // Clear old signatures after 5 seconds to allow same error to be reported again later
        setTimeout(() => capturedErrorSignatures.delete(signature), 5000);
      }

      // Ignore ResizeObserver errors (common browser quirk)
      if (error?.message?.includes("ResizeObserver")) {
        return null;
      }

      // Ignore network errors that are expected (user offline, etc.)
      if (error?.message?.includes("Failed to fetch") ||
          error?.message?.includes("NetworkError")) {
        return null;
      }

      // Add a searchable error code tag based on the event ID
      if (event.event_id) {
        lastSentEventId = event.event_id;
        const errorCode = `ERR-${event.event_id.substring(0, 8).toUpperCase()}`;
        event.tags = event.tags || {};
        event.tags.error_code = errorCode;
      }

      return event;
    },

    // Add useful context
    initialScope: {
      tags: {
        app: "nexus-inventory",
      },
    },
  });
};

// Mark an error as captured (call before captureException)
export const markErrorCaptured = (error) => {
  if (error) {
    capturedErrors.add(error);
  }
};

// Set user context when user logs in (Supabase user object uses 'id' not 'uid')
export const setSentryUser = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

// Capture an error manually with additional context
export const captureError = (error, context = {}) => {
  Sentry.withScope((scope) => {
    // Add any extra context
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    Sentry.captureException(error);
  });
};

// Generate a user-friendly error code from Sentry event ID
export const getErrorCode = (eventId) => {
  // Use the last sent event ID from beforeSend if available (more accurate)
  const idToUse = lastSentEventId || eventId;
  if (!idToUse) return "ERR-UNKNOWN";
  // Take first 8 characters of the event ID for a shorter code
  return `ERR-${idToUse.substring(0, 8).toUpperCase()}`;
};

// Get the last sent event ID (the real one from Sentry)
export const getLastEventId = () => lastSentEventId;

// Capture a message (for non-error events you want to track)
export const captureMessage = (message, level = "info") => {
  Sentry.captureMessage(message, level);
};

export { Sentry };
